/*
 * ZeroWheel logo wall (docs/specs/zw-logo-wall.md, decisions 98–101).
 * - The page draws the first round: six cells, each with a logo. Every logo is in a <template>, in order; each round
 *   moves six places on through them, wrapping to the start, so every logo gets the same time and none shows twice.
 * - Every "seconds per round" a wave runs left to right (on phones, the top row then the bottom): each cell, "wave
 *   delay" after the one before, fades its logo out (ease-in), swaps in the next and fades it in (ease-out). Not a
 *   crossfade: two logos of different widths overlapping look muddy (decision 99).
 * - The next round's logos are loaded and decoded before its wave, so a cell never fades in empty.
 * - It rotates only while on screen and while the tab is visible, and never with reduced motion. It doesn't pause on
 *   hover or focus and has no pause button (decision 101).
 * - Shuffled (data-shuffle), the logos are put in a random order once per page view (decision 138). A picked wall's
 *   first six stay as the page drew them; any other wall's page leaves the cells empty, and the script fills them with
 *   the first round of the shuffled order. One shuffle, so every logo still gets the same time.
 * Markup and styles: blocks/zw-logo-wall.liquid, which loads this script only when there are more than six logos.
 */

/** Fade lengths in milliseconds (decision 99). */
const FADE_OUT = 250;
const FADE_IN = 300;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class ZwLogoWall extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** Every logo, in order (shuffled once, if asked). @type {HTMLImageElement[]} */
  #logos = [];
  /** Cells in the wall, as rendered (six: 6 × 1 on desktop, 3 × 2 on phones). */
  #cellCount = 0;
  /** Where the round on screen starts in #logos. Kept if the wall is moved, since its cells keep their logos. */
  #offset = 0;
  /** The next round: where it starts, its logos, and a promise that settles once they're decoded. */
  /** @type {{ offset: number, images: HTMLImageElement[], ready: Promise<unknown> } | null} */
  #next = null;

  /** Milliseconds per round, and between one cell's change and the next. */
  #interval = 4000;
  #wave = 0;
  #inView = false;
  #waving = false;
  /** @type {number | undefined} */
  #timer;
  /** @type {Set<Animation>} */
  #animations = new Set();

  connectedCallback() {
    const template = this.querySelector(':scope > template');
    if (!(template instanceof HTMLTemplateElement) || this.#controller) return;

    this.#cellCount = this.#cells.length;
    const empty = !this.#cells[0]?.querySelector('img');
    // Read and shuffled only once: a wall moved in the editor keeps its order, as it keeps its round.
    if (!this.#logos.length) {
      this.#logos = /** @type {HTMLImageElement[]} */ ([...template.content.querySelectorAll('img')]);
      if (this.hasAttribute('data-shuffle')) shuffle(this.#logos, empty ? 0 : this.#cellCount);
    }
    if (!this.#cellCount || this.#logos.length <= this.#cellCount) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#interval = Number(this.dataset.interval) || this.#interval;
    this.#wave = Number(this.dataset.wave) || 0;
    if (empty) this.#fill();
    else this.#next = this.#prepare(this.#nextOffset);

    document.addEventListener('visibilitychange', () => this.#schedule(), { signal });
    reducedMotion.addEventListener('change', () => this.#schedule(), { signal });

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      this.#inView = entry.isIntersecting;
      this.#schedule();
    });
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#intersectionObserver?.disconnect();
    this.#inView = false;
    clearTimeout(this.#timer);
    for (const animation of this.#animations) animation.cancel();
    this.#animations.clear();
    this.#waving = false;
  }

  get #cells() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll(':scope > .zw-logo-wall__cells > .zw-logo-wall__cell')]);
  }

  /** Where the round after the one on screen starts. */
  get #nextOffset() {
    return (this.#offset + this.#cellCount) % this.#logos.length;
  }

  get #rotating() {
    return this.#inView && !document.hidden && !reducedMotion.matches;
  }

  /**
   * Starts a full round's timer while the wall should rotate; clears it otherwise, and once it's off the page. Not
   * while a wave or the first fill runs: each schedules the next round itself.
   */
  #schedule() {
    clearTimeout(this.#timer);
    this.#timer = undefined;
    if (!this.#controller || !this.#rotating || this.#waving) return;
    this.#timer = window.setTimeout(() => this.#advance(), this.#interval);
  }

  /**
   * Copies a round's logos into the page's document and starts loading them. Imported, not cloned: a clone stays in
   * the template's inert document and never loads. Not on the page yet, so they can't load lazily.
   * @param {number} offset Where the round starts in #logos.
   */
  #prepare(offset) {
    const images = Array.from({ length: this.#cellCount }, (_, index) => {
      const image = /** @type {HTMLImageElement} */ (
        document.importNode(this.#logos[(offset + index) % this.#logos.length], false)
      );
      image.loading = 'eager';
      return image;
    });
    // A logo that fails to load still takes its turn, as an empty cell, rather than stopping the wall.
    const ready = Promise.all(images.map((image) => image.decode().catch(() => {})));
    return { offset, images, ready };
  }

  /** Fills the cells the page left empty with the first round, faded in as a wave, before any round is timed. */
  async #fill() {
    if (!this.#controller) return;
    const { signal } = this.#controller;
    this.#waving = true;
    const first = this.#prepare(this.#offset);
    await first.ready;
    // Moved or removed meanwhile: a wall put back on the page fills itself again.
    if (signal.aborted) return;

    this.#cells.forEach((cell, index) => {
      const image = first.images[index];
      cell.append(image);
      if (reducedMotion.matches) return;
      this.#animate(image, [{ opacity: 0 }, { opacity: 1 }], {
        duration: FADE_IN,
        delay: index * this.#wave,
        easing: 'ease-out',
        fill: 'backwards',
      });
    });

    this.#next = this.#prepare(this.#nextOffset);
    this.#waving = false;
    this.#schedule();
  }

  async #advance() {
    const next = this.#next;
    if (!next) return;
    this.#waving = true;
    await next.ready;
    if (!this.#controller || !this.#rotating) {
      this.#waving = false;
      this.#schedule();
      return;
    }

    // Rounds are timed from the start of one wave to the next.
    this.#waving = false;
    this.#schedule();

    this.#offset = next.offset;
    this.#next = this.#prepare(this.#nextOffset);
    this.#cells.forEach((cell, index) => this.#swap(cell, next.images[index], index * this.#wave));
  }

  /**
   * Fades a cell's logo out, swaps in the next one and fades it in.
   * @param {HTMLElement} cell
   * @param {HTMLImageElement | undefined} image
   * @param {number} delay
   */
  async #swap(cell, image, delay) {
    const current = cell.querySelector('img');
    if (!current || !image) return;

    const fadeOut = this.#animate(current, [{ opacity: 1 }, { opacity: 0 }], {
      duration: FADE_OUT,
      delay,
      easing: 'ease-in',
      fill: 'forwards',
    });
    if (!(await fadeOut)) return;

    current.replaceWith(image);
    this.#animate(image, [{ opacity: 0 }, { opacity: 1 }], { duration: FADE_IN, easing: 'ease-out' });
  }

  /**
   * Runs an animation, tracked so it can be cancelled; resolves true once it finishes, false if it's cancelled.
   * @param {Element} element
   * @param {Keyframe[]} keyframes
   * @param {KeyframeAnimationOptions} options
   */
  async #animate(element, keyframes, options) {
    const animation = element.animate(keyframes, options);
    this.#animations.add(animation);
    try {
      await animation.finished;
      return true;
    } catch {
      return false;
    } finally {
      this.#animations.delete(animation);
    }
  }
}

/**
 * Shuffles a list in place from `start` on, leaving the items before it where they are (Fisher–Yates).
 * @param {unknown[]} list
 * @param {number} start
 */
function shuffle(list, start) {
  for (let index = list.length - 1; index > start; index--) {
    const other = start + Math.floor(Math.random() * (index - start + 1));
    [list[index], list[other]] = [list[other], list[index]];
  }
}

if (!customElements.get('zw-logo-wall')) {
  customElements.define('zw-logo-wall', ZwLogoWall);
}
